(ns mangos.svg-flex-views
  (:require [dnd.util :refer [queue-microtask]]
            ["react" :as react]
            [mangos.yogalayout :as layout]
            ["pubsub-js" :as PubSub]
            [reagent.impl.component :refer [extract-props
                                            extract-children]]
            [reagent.core :as r]
            [applied-science.js-interop :as j]))

(set! (.-immediateExceptions PubSub) true)

(defonce uniqid-counter (atom 0))

(defn uniqid
  []
  (swap! uniqid-counter inc)
  (str "svg-flex-views:" @uniqid-counter))

(def FlexLayoutEngineContext (react/createContext #js {:layoutEngine nil
                                                       :parentViewId nil
                                                       :subscribe identity
                                                       :unsubscribe identity
                                                       :publish identity}))
(set! (.-displayName FlexLayoutEngineContext) "SVGFlexViewsLayoutEngineContext")

(def FlexLayoutEngineProvider (.-Provider FlexLayoutEngineContext))

(def pubsub-event-names
  {:on-layout "svg-flex-views:on-layout"
   :trigger-layout "svg-flex-views:trigger-layout"})

(defn pubsub-event-name
  [prefix topic]
  (str prefix ":" (get pubsub-event-names topic)))

(def layout-props
  #{:flexDirection
    :justifyContent
    :alignContent
    :alignItems
    :alignSelf
    :flexWrap
    :flex
    :flexGrow
    :flexShrink
    :flexBasis
    :margin
    :marginTop
    :marginLeft
    :marginRight
    :marginBottom
    :padding
    :paddingTop
    :paddingLeft
    :paddingRight
    :paddingBottom
    :border
    :width
    :maxWidth
    :height
    :maxHeight
    :position
    :top
    :left
    :bottom
    :right})

(defn layout-only-style?
  [style]
  (every? #(contains? layout-props %) (keys style)))

(defn extract-layout-props
  [style-props]
  (reduce-kv (fn [acc k v]
               (if (contains? layout-props k)
                 (assoc-in acc [:layout k] v)
                 (assoc-in acc [:style k] v)))
             {:layout {}
              :style {}}
             style-props))

(defn root-view
  [{:keys [height width on-layout]
    :or {height "auto"
         width "auto"}}]
  (let [view-id layout/root-view-id

        on-layout-fn (atom on-layout)

        pubsub-prefix (uniqid)
        layout-engine (layout/make-layout-engine)
        layout-engine-context #js {:layoutEngine layout-engine
                                   :parentViewId view-id
                                   :subscribe (fn [topic f]
                                                (let [topic' (pubsub-event-name pubsub-prefix
                                                                                topic)]
                                                  (.subscribe PubSub topic' f)))
                                   :unsubscribe (fn [token]
                                                  (.unsubscribe PubSub token))
                                   :publish (fn [topic data]
                                              (let [topic' (pubsub-event-name pubsub-prefix
                                                                              topic)]
                                                (.publishSync PubSub topic' data)))}

        layout-version (atom 0)
        handle-dirty! (fn []
                        (let [computed (layout/compute-absolute-view-layouts! layout-engine
                                                                              {:view-id view-id})
                              view-layouts (:view-layouts computed)]
                          (swap! layout-version inc)
                          (.publishSync PubSub
                                        (pubsub-event-name pubsub-prefix
                                                           (:on-layout pubsub-event-names))
                                        {:id (rand-int 10000)
                                         :layout-version @layout-version
                                         :view-layouts view-layouts})

                          (when-let [f @on-layout-fn]
                            (let [root-layout (get view-layouts view-id)]
                              (f root-layout)))

                          ;; flush so that all layout updates that components have made
                          ;; to their local state happen this frame, not next frame
                          (r/flush)))

        layout-scheduled? (atom false)
        schedule-layout! (fn []
                           (when-not @layout-scheduled?
                             (queue-microtask
                               (fn []
                                 (handle-dirty!)
                                 (reset! layout-scheduled? false))))
                           (reset! layout-scheduled? true))

        subscribe-tokens (atom #{})
        subscribe-events! (fn []
                            (swap! subscribe-tokens conj
                                   (.subscribe PubSub
                                               (pubsub-event-name pubsub-prefix
                                                                  (:trigger-layout pubsub-event-names))
                                               schedule-layout!)))
        unsubscribe-events! (fn []
                              (doseq [token @subscribe-tokens]
                                (.unsubscribe PubSub token)))]
    (r/create-class
      {:displayName "svg-flex-views/root-view"
       :unsafe-component-will-mount
       (fn []
         (layout/register-root-view! layout-engine
                                     {:view-id view-id
                                      :style {:height height
                                              :width width}})
         (subscribe-events!)
         (handle-dirty!))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [props (reagent.impl.component/extract-props argv)]
           (reset! on-layout-fn (:on-layout props))))

       :component-will-unmount
       (fn []
         (layout/free! layout-engine)
         (unsubscribe-events!))

       :reagent-render
       (fn [{:keys [x
                    y]}]
         (if (and x y)
           [:> FlexLayoutEngineProvider {:value layout-engine-context}
            (into [:g {:transform (str "translate(" x "," y ")")
                       :data-x x
                       :data-y y}]
                  (r/children (r/current-component)))]
           (into [:> FlexLayoutEngineProvider {:value layout-engine-context}]
                 (r/children (r/current-component)))))})))

(defn view
  [{:keys [view-id on-layout-change]}]
  (let [view-id (or view-id (uniqid))
        state (r/atom {:layout nil
                       :last-seen-layout-version nil})
        handle-layout! (fn [_ {:keys [layout-version view-layouts]}]
                         (when (contains? view-layouts view-id)
                           (let [computed-layout (get view-layouts view-id)
                                 new-layout {:top (:y computed-layout)
                                             :left (:x computed-layout)
                                             :width (:width computed-layout)
                                             :height (:height computed-layout)}]
                             (when-not (= (:layout @state)
                                          new-layout)
                               (if (fn? on-layout-change)
                                 (on-layout-change new-layout))
                               (swap! state assoc
                                      :layout new-layout
                                      :last-seen-layout-version layout-version)))))
        update-layout! (fn [{:keys [layout-engine publish-fn view-id style]}]
                         (layout/update-view! layout-engine
                                              {:view-id view-id
                                               :style style})
                         (publish-fn (:trigger-layout pubsub-event-names)
                                     {:triggering-view-id view-id}))

        subscription-tokens (atom #{})
        subscribe-events! (fn [subscribe-fn]
                            (swap! subscription-tokens conj
                                   (subscribe-fn (:on-layout pubsub-event-names)
                                                 handle-layout!)))
        unsubscribe-events! (fn [unsubscribe-fn]
                              (doseq [token @subscription-tokens]
                                (unsubscribe-fn token)))
        child-layout-engine-context (atom nil)]
    (r/create-class
      {:displayName "svg-flex-views/view"
       :context-type FlexLayoutEngineContext
       :unsafe-component-will-mount
       (fn [this]
         (let [{:keys [style]} (r/props this)
               layout-engine (j/get (.-context this) :layoutEngine)
               parent-view-id (j/get (.-context this) :parentViewId)
               subscribe-fn (j/get (.-context this) :subscribe)
               unsubscribe-fn (j/get (.-context this) :unsubscribe)
               publish-fn (j/get (.-context this) :publish)]
           (reset! child-layout-engine-context #js {:layoutEngine layout-engine
                                                    :parentViewId view-id
                                                    :subscribe subscribe-fn
                                                    :unsubscribe unsubscribe-fn
                                                    :publish publish-fn})
           (layout/add-child-view! layout-engine
                                   {:parent-view-id parent-view-id
                                    :view-id view-id})
           (subscribe-events! subscribe-fn)
           (update-layout! {:layout-engine layout-engine
                            :publish-fn publish-fn
                            :view-id view-id
                            :style style})))

       :component-will-unmount
       (fn [this]
         (let [unsubscribe-fn (j/get (.-context this) :unsubscribe)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (layout/remove-child-view! layout-engine {:view-id view-id})
           (unsubscribe-events! unsubscribe-fn)
           (publish-fn {:trigger-layout pubsub-event-names}
                       {:triggering-view-id view-id})))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [prev-props (r/props this)
               props (extract-props argv)
               props-identical? (= prev-props props)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (when-not props-identical?
             ;; Any props change could theoretically lead to a layout change - e.g. toggling
             ;; a :visible? prop would definitely do it (even if this is passed down to other
             ;; children we have to worry?)
             ;;
             ;; do we also have to compare children here?
             (let [{:keys [style]} props]
               (update-layout! {:layout-engine layout-engine
                                :publish-fn publish-fn
                                :view-id view-id
                                :style style})))))

       :reagent-render
       (fn [{:keys [style] :as props}]
         (let [{:keys [layout]} @state
               children (filterv identity (r/children (r/current-component)))
               modified-children (->> (map-indexed (fn [_ child]
                                                     (let [child-tag (nth child 0)
                                                           child-props (or (extract-props child) {})
                                                           child-children (extract-children child)]
                                                       (into [child-tag child-props] child-children)))
                                                   children)
                                      (into []))
               {resolved-style :style} (extract-layout-props style)
               resolved-layout {:x (:left layout)
                                :y (:top layout)
                                :width (:width layout)
                                :height (:height layout)}
               resolved-props (-> props
                                  (merge resolved-layout)
                                  (assoc :style resolved-style)
                                  (dissoc :view-debug))]
           [:> FlexLayoutEngineProvider {:value @child-layout-engine-context}
            (into [:g (or (:g-props resolved-props) {})
                   (when (and layout
                              (or (not (layout-only-style? resolved-style))
                                  (contains? resolved-props :class)))
                     [:rect (-> resolved-props
                                (dissoc :view-id))])
                   (when (:view-debug props)
                     [:rect {:data-view-id view-id
                             :stroke (or (:debug-color props) "rgba(255,0,0,1)")
                             :fill "transparent"
                             :x (:x resolved-layout)
                             :y (:y resolved-layout)
                             :width (:width resolved-layout)
                             :height (:height resolved-layout)}])]
                  modified-children)]))})))

(defn image
  [{:keys [view-id]}]
  (let [view-id (or view-id (uniqid))
        state (r/atom {:layout nil
                       :last-seen-layout-version nil})
        handle-layout! (fn [_ {:keys [layout-version view-layouts]}]
                         (when (contains? view-layouts view-id)
                           (let [computed-layout (get view-layouts view-id)
                                 new-layout {:top (:y computed-layout)
                                             :left (:x computed-layout)
                                             :width (:width computed-layout)
                                             :height (:height computed-layout)}]
                             (when-not (= (:layout @state)
                                          new-layout)
                               (swap! state assoc
                                      :layout new-layout
                                      :last-seen-layout-version layout-version)))))
        update-layout! (fn [{:keys [layout-engine publish-fn view-id style]}]
                         (layout/update-view! layout-engine
                                              {:view-id view-id
                                               :style style})
                         (publish-fn (:trigger-layout pubsub-event-names)
                                     {:triggering-view-id view-id}))

        subscription-tokens (atom #{})
        subscribe-events! (fn [subscribe-fn]
                            (swap! subscription-tokens conj
                                   (subscribe-fn (:on-layout pubsub-event-names)
                                                 handle-layout!)))
        unsubscribe-events! (fn [unsubscribe-fn]
                              (doseq [token @subscription-tokens]
                                (unsubscribe-fn token)))]
    (r/create-class
      {:displayName "svg-flex-views/image"
       :context-type FlexLayoutEngineContext
       :unsafe-component-will-mount
       (fn [this]
         (let [{:keys [style]} (r/props this)
               layout-engine (j/get (.-context this) :layoutEngine)
               parent-view-id (j/get (.-context this) :parentViewId)
               publish-fn (j/get (.-context this) :publish)
               subscribe-fn (j/get (.-context this) :subscribe)]
           (layout/add-child-view! layout-engine
                                   {:parent-view-id parent-view-id
                                    :view-id view-id
                                    :tag :image})
           (subscribe-events! subscribe-fn)
           (update-layout! {:layout-engine layout-engine
                            :publish-fn publish-fn
                            :view-id view-id
                            :style style})))

       :component-will-unmount
       (fn [this]
         (let [unsubscribe-fn (j/get (.-context this) :unsubscribe)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (layout/remove-child-view! layout-engine {:view-id view-id})
           (unsubscribe-events! unsubscribe-fn)
           (publish-fn {:trigger-layout pubsub-event-names}
                       {:triggering-view-id view-id})))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [prev-props (r/props this)
               props (extract-props argv)
               props-identical? (= prev-props props)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (when-not props-identical?
             (let [{:keys [style]} props]
               (update-layout! {:layout-engine layout-engine
                                :publish-fn publish-fn
                                :view-id view-id
                                :style style})))))

       :reagent-render
       (fn [{:keys [style] :as props}]
         (let [{:keys [layout]} @state
               {extracted-style :style} (extract-layout-props style)
               resolved-style (merge extracted-style
                                     {:position "absolute"})
               resolved-layout {:x (:left layout)
                                :y (:top layout)
                                :width (:width layout)
                                :height (:height layout)}
               resolved-props (-> props
                                  (merge resolved-layout)
                                  (assoc :style resolved-style)
                                  (dissoc :view-debug))]
           (when layout
             [:<>
              [:image (-> resolved-props
                          (dissoc :view-id))]
              (when (:view-debug props)
                [:rect {:data-view-id view-id
                        :stroke (or (:debug-color props) "rgba(0,0,0,1)")
                        :fill "transparent"
                        :x (:x resolved-layout)
                        :y (:y resolved-layout)
                        :width (:width resolved-layout)
                        :height (:height resolved-layout)}])])))})))

(defn icon
  [{:keys [view-id]}]
  (let [view-id (or view-id (uniqid))
        state (r/atom {:layout nil
                       :last-seen-layout-version nil})
        handle-layout! (fn [_ {:keys [layout-version view-layouts]}]
                         (when (contains? view-layouts view-id)
                           (let [computed-layout (get view-layouts view-id)
                                 new-layout {:top (:y computed-layout)
                                             :left (:x computed-layout)
                                             :width (:width computed-layout)
                                             :height (:height computed-layout)}]
                             (when-not (= (:layout @state)
                                          new-layout)
                               (swap! state assoc
                                      :layout new-layout
                                      :last-seen-layout-version layout-version)))))
        update-layout! (fn [{:keys [layout-engine publish-fn view-id style]}]
                         (layout/update-view! layout-engine
                                              {:view-id view-id
                                               :style style})
                         (publish-fn (:trigger-layout pubsub-event-names)
                                     {:triggering-view-id view-id}))

        subscription-tokens (atom #{})
        subscribe-events! (fn [subscribe-fn]
                            (swap! subscription-tokens conj
                                   (subscribe-fn (:on-layout pubsub-event-names)
                                                 handle-layout!)))
        unsubscribe-events! (fn [unsubscribe-fn]
                              (doseq [token @subscription-tokens]
                                (unsubscribe-fn token)))]
    (r/create-class
      {:displayName "svg-flex-views/icon"
       :context-type FlexLayoutEngineContext
       :unsafe-component-will-mount
       (fn [this]
         (let [{:keys [style]} (r/props this)
               layout-engine (j/get (.-context this) :layoutEngine)
               parent-view-id (j/get (.-context this) :parentViewId)
               publish-fn (j/get (.-context this) :publish)
               subscribe-fn (j/get (.-context this) :subscribe)]
           (layout/add-child-view! layout-engine
                                   {:parent-view-id parent-view-id
                                    :view-id view-id
                                    :tag :icon})
           (subscribe-events! subscribe-fn)
           (update-layout! {:layout-engine layout-engine
                            :publish-fn publish-fn
                            :view-id view-id
                            :style style})))

       :component-will-unmount
       (fn [this]
         (let [unsubscribe-fn (j/get (.-context this) :unsubscribe)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (layout/remove-child-view! layout-engine {:view-id view-id})
           (unsubscribe-events! unsubscribe-fn)
           (publish-fn {:trigger-layout pubsub-event-names}
                       {:triggering-view-id view-id})))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [prev-props (r/props this)
               props (extract-props argv)
               props-identical? (= prev-props props)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (when-not props-identical?
             (let [{:keys [style]} props]
               (update-layout! {:layout-engine layout-engine
                                :publish-fn publish-fn
                                :view-id view-id
                                :style style})))))

       :reagent-render
       (fn [{:keys [style] :as props}]
         (let [{:keys [layout]} @state
               {extracted-style :style} (extract-layout-props style)
               resolved-style (merge extracted-style
                                     {:position "absolute"})
               resolved-layout {:x (:left layout)
                                :y (:top layout)
                                :width (:width layout)
                                :height (:height layout)}
               icon-comp (:icon-comp props)
               color (:color props)
               resolved-props (-> props
                                  (merge resolved-layout)
                                  (assoc :style resolved-style)
                                  (dissoc :icon-comp
                                          :color
                                          :view-debug))]
           (when layout
             [:svg resolved-props
              [:> icon-comp {:style {:fill color}}]
              (when (:view-debug props)
                [:rect {:data-view-id view-id
                        :stroke (or (:debug-color props) "rgba(0,0,0,1)")
                        :fill "transparent"
                        :x 0
                        :y 0
                        :width (:width resolved-layout)
                        :height (:height resolved-layout)}])])))})))

(defn multiline-text
  [{:keys [view-id content]}]
  (let [view-id (or view-id (uniqid))
        state (r/atom {:layout nil
                       :last-seen-layout-version nil
                       ;; text content
                       :content content
                       :text-lines []})
        handle-layout! (fn [this _ {:keys [layout-version view-layouts]}]
                         (when (contains? view-layouts view-id)
                           (let [computed-layout (get view-layouts view-id)
                                 new-layout {:top (:y computed-layout)
                                             :left (:x computed-layout)
                                             :width (:width computed-layout)
                                             :height (:height computed-layout)}
                                 props (r/props this)
                                 new-content (get props :content)
                                 needs-layout? (or (not= (:content @state)
                                                         new-content)
                                                   (not= (:layout @state)
                                                         new-layout))]
                             ;; if the calculated layout has changed update the size
                             ;; that we render at, and also calculate the lines of
                             ;; text that we need to render

                             ;; #TODO - we need to calculate the new lines if the text
                             ;;         content has changed, even if the layout has
                             ;;         not changed
                             (when needs-layout?
                               (let [props (r/props this)
                                     text (:content props)
                                     style (:style props)
                                     width (:width new-layout)
                                     updated-metrics (layout/text-metrics
                                                       {:text text
                                                        :style (assoc style
                                                                 :width width)})]
                                 (swap! state assoc
                                        :layout new-layout
                                        :last-seen-layout-version layout-version
                                        :text-lines (j/get updated-metrics :lines)))))))
        update-layout! (fn [{:keys [layout-engine publish-fn view-id style content]}]
                         (layout/update-view! layout-engine
                                              {:view-id view-id
                                               :style style
                                               :text-content content})
                         (publish-fn (:trigger-layout pubsub-event-names)
                                     {:triggering-view-id view-id}))

        subscription-tokens (atom #{})
        subscribe-events! (fn [this subscribe-fn]
                            (swap! subscription-tokens conj
                                   (subscribe-fn (:on-layout pubsub-event-names)
                                                 (partial handle-layout! this))))
        unsubscribe-events! (fn [unsubscribe-fn]
                              (doseq [token @subscription-tokens]
                                (unsubscribe-fn token)))]
    (r/create-class
      {:displayName "svg-flex-views/multiline-text"
       :context-type FlexLayoutEngineContext
       :unsafe-component-will-mount
       (fn [this]
         (let [{:keys [style content]} (r/props this)
               layout-engine (j/get (.-context this) :layoutEngine)
               parent-view-id (j/get (.-context this) :parentViewId)
               publish-fn (j/get (.-context this) :publish)
               subscribe-fn (j/get (.-context this) :subscribe)]
           (layout/add-child-view! layout-engine
                                   {:parent-view-id parent-view-id
                                    :view-id view-id
                                    :tag :text})
           (subscribe-events! this subscribe-fn)
           (update-layout! {:layout-engine layout-engine
                            :publish-fn publish-fn
                            :view-id view-id
                            :style style
                            :content content})))

       :component-will-unmount
       (fn [this]
         (let [unsubscribe-fn (j/get (.-context this) :unsubscribe)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (layout/remove-child-view! layout-engine {:view-id view-id})
           (unsubscribe-events! unsubscribe-fn)
           (publish-fn {:trigger-layout pubsub-event-names}
                       {:triggering-view-id view-id})))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [prev-props (r/props this)
               props (extract-props argv)
               props-identical? (= prev-props props)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (when-not props-identical?
             (let [{:keys [style]} props]
               (update-layout! {:layout-engine layout-engine
                                :publish-fn publish-fn
                                :view-id view-id
                                :style style
                                :content (:content props)})))))

       :reagent-render
       (fn [{:keys [style] :as props}]
         (let [{:keys [layout]} @state
               {extracted-style :style} (extract-layout-props style)
               line-height-pixels (:lineHeight extracted-style)
               resolved-style (merge extracted-style
                                     {:position "absolute"})
               resolved-layout {:x (:left layout)
                                :y (:top layout)
                                :width (:width layout)
                                :height (:height layout)}
               text-align (get resolved-style :textAlign "left")]
           (when (and layout (:text-lines @state))
             [:svg (-> (select-keys resolved-layout [:x :y :width :height])
                       (assoc :style {:pointer-events "none"}))
              ;; visual rect for debugging
              (when (:view-debug props)
                [:rect {:fill "rgba(0,0,0,0.2)"
                        :x 0
                        :y 0
                        :width (:width resolved-layout)
                        :height (:height resolved-layout)}])
              ;; NOTE: want to use dominantBaseline "text-before-edge" here,
              ;; but can't as there is no support for this on Edge. Instead
              ;; wrapping the text in a `tspan` and shifting by 1em
              ;; which seems to be roughly right, but will probably lead
              ;; to a good few edge cases
              #_(assoc resolved-style :dominantBaseline "text-before-edge")
              (into [:text {:style resolved-style}]
                    (map-indexed (fn [i line]
                                   [:tspan (cond-> {:x (if (= text-align "center")
                                                         (/ (get resolved-layout :width 0) 2)
                                                         0)
                                                    :y (* i line-height-pixels)
                                                    :height line-height-pixels
                                                    :dy "1em"}
                                                   (= text-align "center")
                                                   (assoc :text-anchor "middle"))
                                    line])
                                 (:text-lines @state)))])))})))

(defn text
  [{:keys [view-id]}]
  (let [view-id (or view-id (uniqid))
        state (r/atom {:layout nil
                       :last-seen-layout-version nil})
        handle-layout! (fn [_ {:keys [layout-version view-layouts]}]
                         (when (contains? view-layouts view-id)
                           (let [computed-layout (get view-layouts view-id)
                                 new-layout {:top (:y computed-layout)
                                             :left (:x computed-layout)
                                             :width (:width computed-layout)
                                             :height (:height computed-layout)}]
                             (when-not (= (:layout @state)
                                          new-layout)
                               (swap! state assoc
                                      :layout new-layout
                                      :last-seen-layout-version layout-version)))))
        update-layout! (fn [{:keys [layout-engine publish-fn view-id style content]}]
                         (layout/update-view! layout-engine
                                              {:view-id view-id
                                               :style style
                                               :text-content content})
                         (publish-fn (:trigger-layout pubsub-event-names)
                                     {:triggering-view-id view-id}))

        subscription-tokens (atom #{})
        subscribe-events! (fn [subscribe-fn]
                            (swap! subscription-tokens conj
                                   (subscribe-fn (:on-layout pubsub-event-names)
                                                 handle-layout!)))
        unsubscribe-events! (fn [unsubscribe-fn]
                              (doseq [token @subscription-tokens]
                                (unsubscribe-fn token)))]
    (r/create-class
      {:displayName "svg-flex-views/text"
       :context-type FlexLayoutEngineContext
       :unsafe-component-will-mount
       (fn [this]
         (let [{:keys [style content]} (r/props this)
               layout-engine (j/get (.-context this) :layoutEngine)
               parent-view-id (j/get (.-context this) :parentViewId)
               publish-fn (j/get (.-context this) :publish)
               subscribe-fn (j/get (.-context this) :subscribe)]
           (layout/add-child-view! layout-engine
                                   {:parent-view-id parent-view-id
                                    :view-id view-id
                                    :tag :text})
           (subscribe-events! subscribe-fn)
           (update-layout! {:layout-engine layout-engine
                            :publish-fn publish-fn
                            :view-id view-id
                            :style style
                            :content content})))

       :component-will-unmount
       (fn [this]
         (let [unsubscribe-fn (j/get (.-context this) :unsubscribe)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (layout/remove-child-view! layout-engine {:view-id view-id})
           (unsubscribe-events! unsubscribe-fn)
           (publish-fn {:trigger-layout pubsub-event-names}
                       {:triggering-view-id view-id})))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [prev-props (r/props this)
               props (extract-props argv)
               props-identical? (= prev-props props)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (when-not props-identical?
             (let [{:keys [style]} props]
               (update-layout! {:layout-engine layout-engine
                                :publish-fn publish-fn
                                :view-id view-id
                                :style style
                                :content (:content props)})))))

       :reagent-render
       (fn [{:keys [style content] :as props}]
         (let [{:keys [layout]} @state
               {extracted-style :style} (extract-layout-props style)
               resolved-style (merge extracted-style
                                     {:position "absolute"})
               resolved-layout {:x (:left layout)
                                :y (:top layout)
                                :width (:width layout)
                                :height (:height layout)}
               resolved-props (-> props
                                  (merge resolved-layout)
                                  (assoc :style resolved-style))]
           (when layout
             [:g
              (when (:view-debug props)
                [:rect {:fill "rgba(0,0,0,0.2)"
                        :x (:x resolved-layout)
                        :y (:y resolved-layout)
                        :width (:width resolved-layout)
                        :height (:height resolved-layout)}])
              [:text (-> resolved-props
                         (dissoc :view-id
                                 :content
                                 :view-debug)
                         ;; NOTE: want to use dominantBaseline "central" here, but
                         ;; can't as there is no support for this on Edge. Instead
                         ;; wrapping the text in a `tspan` and shifting by 1em
                         ;; which seems to be roughly right, but will probably lead
                         ;; to a good few edge cases
                         #_(assoc :dominantBaseline "central")
                         (assoc :dy (/ (:height resolved-layout) 2)))
               [:tspan {:dy "1em"} content]]])))})))

(defn flex-wrapper
  [{:keys [view-id]}]
  (let [view-id (or view-id (uniqid))
        state (r/atom {:layout nil
                       :last-seen-layout-version nil})
        handle-layout! (fn [_ {:keys [layout-version view-layouts]}]
                         (when (contains? view-layouts view-id)
                           (let [computed-layout (get view-layouts view-id)
                                 new-layout {:top (:y computed-layout)
                                             :left (:x computed-layout)
                                             :width (:width computed-layout)
                                             :height (:height computed-layout)}]
                             (when-not (= (:layout @state)
                                          new-layout)
                               (swap! state assoc
                                      :layout new-layout
                                      :last-seen-layout-version layout-version)))))
        update-layout! (fn [{:keys [layout-engine publish-fn view-id style]}]
                         (layout/update-view! layout-engine
                                              {:view-id view-id
                                               :style style})
                         (publish-fn (:trigger-layout pubsub-event-names)
                                     {:triggering-view-id view-id}))

        subscription-tokens (atom #{})
        subscribe-events! (fn [subscribe-fn]
                            (swap! subscription-tokens conj
                                   (subscribe-fn (:on-layout pubsub-event-names)
                                                 handle-layout!)))
        unsubscribe-events! (fn [unsubscribe-fn]
                              (doseq [token @subscription-tokens]
                                (unsubscribe-fn token)))]
    (r/create-class
      {:displayName "svg-flex-views/flex-wrapper"
       :context-type FlexLayoutEngineContext
       :unsafe-component-will-mount
       (fn [this]
         (let [{:keys [style]} (r/props this)
               layout-engine (j/get (.-context this) :layoutEngine)
               parent-view-id (j/get (.-context this) :parentViewId)
               subscribe-fn (j/get (.-context this) :subscribe)
               publish-fn (j/get (.-context this) :publish)]
           (layout/add-child-view! layout-engine
                                   {:parent-view-id parent-view-id
                                    :view-id view-id})
           (subscribe-events! subscribe-fn)
           (update-layout! {:layout-engine layout-engine
                            :publish-fn publish-fn
                            :view-id view-id
                            :style style})))

       :component-will-unmount
       (fn [this]
         (let [unsubscribe-fn (j/get (.-context this) :unsubscribe)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (layout/remove-child-view! layout-engine {:view-id view-id})
           (unsubscribe-events! unsubscribe-fn)
           (publish-fn {:trigger-layout pubsub-event-names}
                       {:triggering-view-id view-id})))

       :unsafe-component-will-receive-props
       (fn [this argv]
         (let [prev-props (r/props this)
               props (extract-props argv)
               props-identical? (= prev-props props)
               layout-engine (j/get (.-context this) :layoutEngine)
               publish-fn (j/get (.-context this) :publish)]
           (when-not props-identical?
             ;; Any props change could theoretically lead to a layout change - e.g. toggling
             ;; a :visible? prop would definitely do it (even if this is passed down to other
             ;; children we have to worry?)
             ;;
             ;; do we also have to compare children here?
             (let [{:keys [style]} props]
               (update-layout! {:layout-engine layout-engine
                                :publish-fn publish-fn
                                :view-id view-id
                                :style style})))))

       :reagent-render
       (fn [{:keys [render-child]}]
         (let [{:keys [layout]} @state
               resolved-layout {:x (:left layout)
                                :y (:top layout)
                                :width (:width layout)
                                :height (:height layout)}]
           (render-child resolved-layout)))})))
